/**
 * The Sources panel on the Tests › Proposed tab: documents, links, and
 * issues from the app's issue tracker, each added from one button row.
 *
 * Lives in `pages/Tests/` because it is this tab's panel, not a shared
 * component. It wraps `ReferenceDocsSection`, which stays about documents,
 * and adds Import issues through that section's two slots: the button goes
 * in the row, and the issue list, or the reason it can't open yet, goes
 * right under it.
 *
 * Whether the issue list is open is held here and nowhere else.
 * `CandidatesSection` unmounts this panel when Sources closes, so the list
 * starts closed every time Sources opens. Holding it on the Tests page was
 * rejected: the page outlives the panel, so the list could reappear open.
 *
 * Linking a tracker stays on the app's Integrations tab, so there is one
 * place to set it up. This panel only says which step is missing and links
 * to where it is done.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Import } from 'lucide-react'
import { Button } from '@/components/ui'
import { ReferenceDocsSection } from '@/components/ReferenceDocsSection'
import { IssueSourceBrowser } from '@/pages/AppIssueSourceCard'
import { useWorkspacePath } from '@/hooks/useWorkspacePath'
import { trackerNameList, useTrackerDescriptors } from '@/hooks/useTrackerDescriptors'
import type { ReferenceDoc } from '@/types'

/** What the Tests page knows about the app's issue tracker, read from the
 * app's integrations. `connected` with no project keys means a tracker is
 * connected on the workspace but this app is not linked to it. */
export type IssueSourceState =
  | { kind: 'loading' }
  | { kind: 'failed' }
  | { kind: 'noConnection' }
  | { kind: 'connected'; projectKeys: string[] }

const LINK_CLASS = 'font-semibold text-primary underline underline-offset-2'

export function ProposedSources({
  appId,
  selectedVariantId,
  docs,
  totalOriginalCapBytes,
  onDocumentsChange,
  issueSource,
}: {
  appId: string
  selectedVariantId?: string
  docs: ReferenceDoc[]
  totalOriginalCapBytes: number
  onDocumentsChange: () => void | Promise<void>
  issueSource: IssueSourceState
}) {
  const wp = useWorkspacePath()
  const [issuesOpen, setIssuesOpen] = useState(false)
  const linkedKeys = issueSource.kind === 'connected' ? issueSource.projectKeys : []
  const linked = linkedKeys.length > 0
  const integrationsUrl = wp(`/applications?app=${appId}&tab=integrations`)

  const openIntegrations = (
    <Link to={integrationsUrl} className={LINK_CLASS}>
      Open Integrations
    </Link>
  )

  let below = null
  if (linked) {
    if (issuesOpen) {
      below = (
        <IssueSourceBrowser
          appId={appId}
          projectKeys={linkedKeys}
          emptyMessage={
            <>
              No issues to import yet. In this app's Integrations section on the Apps page, choose which issues count as
              requirements or defects. {openIntegrations}
            </>
          }
        />
      )
    }
  } else if (issueSource.kind === 'failed') {
    below = (
      <p className="text-xs text-muted-foreground">
        We couldn't load this app's integrations. Refresh the page to try again.
      </p>
    )
  } else if (issueSource.kind === 'noConnection') {
    below = <ConnectTrackerLine settingsUrl={wp('/settings?tab=workspace.integrations')} />
  } else if (issueSource.kind === 'connected') {
    below = (
      <p className="text-xs text-muted-foreground">
        To import issues, link this app to your issue tracker in its Integrations section on the Apps page. {openIntegrations}
      </p>
    )
  }
  // Still loading: greyed out with no line, so the wrong reason never shows.

  return (
    <ReferenceDocsSection
      appId={appId}
      selectedVariantId={selectedVariantId}
      docs={docs}
      totalOriginalCapBytes={totalOriginalCapBytes}
      onChange={onDocumentsChange}
      embedded
      moreActions={
        <Button
          variant="outline"
          size="sm"
          disabled={!linked}
          onClick={() => setIssuesOpen((v) => !v)}
          aria-expanded={linked ? issuesOpen : undefined}
        >
          <Import className="h-3 w-3 mr-1" />
          Import issues
        </Button>
      }
      belowActions={below}
    />
  )
}

/** The line for a workspace with no tracker connected. Its own component so
 * the trackers' names are only asked for when this line shows. */
function ConnectTrackerLine({ settingsUrl }: { settingsUrl: string }) {
  const trackers = trackerNameList(useTrackerDescriptors())
  return (
    <p className="text-xs text-muted-foreground">
      To import issues, first connect {trackers} in Workspace Settings → Integrations.{' '}
      <Link to={settingsUrl} className={LINK_CLASS}>
        Open Workspace Settings
      </Link>
    </p>
  )
}
